import { Box } from "@chakra-ui/react";
import { BatchItem, FILE_STATES } from "@rpldy/uploady";
import { useQueryClient } from "@tanstack/react-query";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { InvestorTransferSetupFooter } from "features/investor-transfer/setup/InvestorTransferSetupFooter";
import { documentStatusUploadCompleteUpdate } from "gen/endpoints/documents/documents";
import { useFundFamilyEventRetrieve } from "gen/endpoints/fund-family-event/fund-family-event";
import {
  useInvestorTransferDocumentsCreate,
  useInvestorTransferDocumentsListSuspense,
  useInvestorTransferDraftRun,
  useInvestorTransferRetrieveSuspense,
} from "gen/endpoints/investor-transfer/investor-transfer";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { DocumentCreateResponseData } from "gen/model/documentCreateResponseData";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import { get, noop } from "lodash-es";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DocumentList } from "shared/components/document/DocumentList";
import { GenericErrorPageView } from "shared/components/empty/GenericErrorPageView";
import Form from "shared/components/form/Form";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageInstructions } from "shared/components/layout/PageInstructions";
import { usePageWizard } from "shared/components/layout/PageWizard";
import { useToast } from "shared/components/toast/useToast";
import {
  FileUpload,
  FileUploadItemFilter,
} from "shared/components/upload/FileUpload";
import {
  uploadAcceptCsvAndExcel,
  uploadAcceptImage,
  uploadAcceptPdf,
} from "shared/components/upload/FileUploadUtils";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { sortDateStringsDesc } from "shared/utils/DateUtils";

export const InvestorTransferSetupDocumentsPage = () => {
  const { t } = useTranslation(["common", "investorTransfer"]);
  const { fundFamilyId, investorTransferId } = useRequiredStringParams();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { navigateNext } = usePageWizard();

  const {
    data: investorTransferData,
    queryKey: investorTransferRetrieveQueryKey,
  } = useInvestorTransferRetrieveSuspense(fundFamilyId, investorTransferId);

  const { data: fundFamilyEventData } = useFundFamilyEventRetrieve(
    fundFamilyId,
    investorTransferData.fund_family_event_id,
    {
      query: {
        enabled: !!investorTransferData.fund_family_event_id,
      },
    },
  );

  const renderToastsForDraftTransferRun = (isError: boolean) => {
    if (isError) {
      showToast({
        message: t(
          "investorTransfer:setup.documents.runDraftTransfer.errorMessage",
        ),
        variant: "error",
      });
    } else {
      showToast({
        message: t(
          "investorTransfer:setup.documents.runDraftTransfer.successMessage",
        ),
        variant: "success",
      });
    }
  };

  const workflowStatuses = useCeleryWorkflowPoll(
    fundFamilyEventData?.celery_workflow_id
      ? [fundFamilyEventData.celery_workflow_id]
      : [],
    renderToastsForDraftTransferRun,
    navigateNext,
    noop,
  );

  const form = useForm({});
  const {
    mutateAsync: draftRunInvestorTransfer,
    isPending: runInvestorTransferLoading,
  } = useInvestorTransferDraftRun({
    mutation: {
      onMutate: () =>
        showToast({
          message: t(
            "investorTransfer:setup.documents.runDraftTransfer.startMessage",
          ),
          variant: "info",
        }),
    },
  });

  const {
    mutateAsync: createInvestorTransferDocuments,
    isPending: createTransferDocumentsIsLoading,
  } = useInvestorTransferDocumentsCreate();

  const { data: listData, refetch: refetchDocuments } =
    useInvestorTransferDocumentsListSuspense(fundFamilyId, investorTransferId, {
      query: {
        select: (data) =>
          data.sort((a, b) => sortDateStringsDesc(a.created_at, b.created_at)),
        refetchInterval: 5_000,
      },
    });

  // FIXME: Can all this be moved to backend SQS listener?
  const onItemFinished = useCallback(
    async (item: BatchItem) => {
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      const document: DocumentCreateResponseData = get(item, "document")!;
      await documentStatusUploadCompleteUpdate(document.id);
      await createInvestorTransferDocuments({
        fundFamilyId,
        investorTransferId,
        data: { document_create_data_list: [{ document_id: document.id }] },
      });
      await refetchDocuments();
    },
    [
      createInvestorTransferDocuments,
      fundFamilyId,
      investorTransferId,
      refetchDocuments,
    ],
  );

  const [uploadItems, setUploadItems] = useState<BatchItem[]>([]);

  const areAllUploadsFinished = uploadItems.every(
    (item) => item.state === FILE_STATES.FINISHED,
  );

  const isDraftTransferProcessing =
    fundFamilyEventData?.celery_workflow_id &&
    (workflowStatuses.get(fundFamilyEventData.celery_workflow_id)?.state ===
      CeleryWorkflowStateMachineFieldsEnum.processing ||
      workflowStatuses.get(fundFamilyEventData.celery_workflow_id)?.state ===
        CeleryWorkflowStateMachineFieldsEnum.draft);

  const isLoading =
    createTransferDocumentsIsLoading ||
    !areAllUploadsFinished ||
    runInvestorTransferLoading ||
    !!isDraftTransferProcessing;

  const itemFilter: FileUploadItemFilter = (item) =>
    !listData.some(
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      (doc) => doc.id === (get(item, "document.id") as string | undefined),
    );

  const isWorkflowFailed =
    fundFamilyEventData?.celery_workflow_id &&
    workflowStatuses.get(fundFamilyEventData.celery_workflow_id)?.state ===
      CeleryWorkflowStateMachineFieldsEnum.failed;

  const rerunDraftTransfer = async () => {
    await draftRunInvestorTransfer({
      fundFamilyId,
      investorTransferId,
    });
    await queryClient.invalidateQueries({
      queryKey: investorTransferRetrieveQueryKey,
    });
  };

  return isWorkflowFailed ? (
    <GenericErrorPageView reset={rerunDraftTransfer} />
  ) : (
    <PageContent contentWidth="boxed">
      <Form
        form={form}
        onSubmit={async () => {
          // adding as a separate call to happen after transferees are set up
          // long term we should add this to a spinner transition page
          await draftRunInvestorTransfer({
            fundFamilyId,
            investorTransferId,
          });
          await queryClient.invalidateQueries({
            queryKey: investorTransferRetrieveQueryKey,
          });
        }}
        onSuccess={noop}
      >
        <PageHeader
          workflowName={t("investorTransfer:setup.superHeading")}
          title={t("investorTransfer:setup.documents.title")}
          badge={getCapitalActivityEventStateBadge(
            fundFamilyEventData?.state,
            t,
          )}
        />
        <PageBody>
          <PageInstructions>
            {t("investorTransfer:setup.documents.description")}
          </PageInstructions>
          <Box mb="24px">
            <FileUpload
              documentType={DocumentTypeEnum.investor_transfer}
              accept={{
                ...uploadAcceptCsvAndExcel,
                ...uploadAcceptPdf,
                ...uploadAcceptImage,
              }}
              onItemsChanged={setUploadItems}
              onItemFinished={onItemFinished}
              itemFilter={itemFilter}
            />
            <DocumentList
              documents={listData}
              onDocumentDeleted={() => refetchDocuments()}
            />
          </Box>
          <InvestorTransferSetupFooter
            fundFamilyId={fundFamilyId}
            allowBack={true}
            isLoading={isLoading}
            continueButtonText={t("investorTransfer:setup.review.title")}
          />
        </PageBody>
      </Form>
    </PageContent>
  );
};
